import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { FiPlus, FiEdit, FiTrash } from 'react-icons/fi';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import ResponsiveDataList from '../components/common/ResponsiveDataList';
import MobileCard from '../components/common/MobileCard';

const ReligionPage = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [religions, setReligions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingReligion, setEditingReligion] = useState(null);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const fetchReligions = async () => {
    const res = await api.get('/religions');
    setReligions(res.data.data || []);
  };

  useEffect(() => {
    fetchReligions();
  }, []);

  const onSubmit = async (data) => {
    if (editingReligion) {
      await api.put(`/religions/${editingReligion.id}`, data);
    } else {
      await api.post('/religions', data);
    }
    setShowModal(false);
    setEditingReligion(null);
    reset();
    fetchReligions();
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Religions</h1>
        <ConditionalButton
          entity="religions"
          permission="create"
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={() => setShowModal(true)}
        >
          <FiPlus />
          <span>Nouvelle religion</span>
        </ConditionalButton>
      </div>
      <ResponsiveDataList
        isLoading={false}
        isEmpty={religions.length === 0}
        emptyMessage="Aucune religion"
        mobileCards={religions.map((rel) => (
          <MobileCard
            key={rel.id}
            title={rel.religion}
            actions={
              <ConditionalActions
                entity="religions"
                onEdit={() => {
                  setEditingReligion(rel);
                  setShowModal(true);
                  setValue('religion', rel.religion);
                }}
                onDelete={async () => {
                  const result = await Swal.fire({
                    title: 'Supprimer cette religion ?',
                    text: 'Cette action est irréversible.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Oui, supprimer',
                    cancelButtonText: 'Annuler',
                  });
                  if (result.isConfirmed) {
                    await api.delete(`/religions/${rel.id}`);
                    fetchReligions();
                    Swal.fire('Supprimé !', 'La religion a été supprimée.', 'success');
                  }
                }}
                editIcon={FiEdit}
                deleteIcon={FiTrash}
                className="flex space-x-2"
              />
            }
          />
        ))}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Religion</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {religions.length === 0 ? (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center text-gray-500">Aucune religion</td>
              </tr>
            ) : (
              religions.map((rel) => (
                <tr key={rel.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rel.religion}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <ConditionalActions
                      entity="religions"
                      onEdit={() => {
                        setEditingReligion(rel);
                        setShowModal(true);
                        setValue('religion', rel.religion);
                      }}
                      onDelete={async () => {
                        const result = await Swal.fire({
                          title: 'Supprimer cette religion ?',
                          text: 'Cette action est irréversible.',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#d33',
                          cancelButtonColor: '#3085d6',
                          confirmButtonText: 'Oui, supprimer',
                          cancelButtonText: 'Annuler',
                        });
                        if (result.isConfirmed) {
                          await api.delete(`/religions/${rel.id}`);
                          fetchReligions();
                          Swal.fire('Supprimé !', 'La religion a été supprimée.', 'success');
                        }
                      }}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ResponsiveDataList>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">{editingReligion ? 'Modifier' : 'Ajouter une'} religion</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Religion *</label>
                <input {...register('religion', { required: true })} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
                {errors.religion && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 rounded-lg" onClick={() => { setShowModal(false); setEditingReligion(null); }}>Annuler</button>
                <ConditionalButton
                  entity="religions"
                  permission={editingReligion ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReligionPage;
